import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  /** Extra classes for the outer clipping container. */
  className?: string;
  /** Extra classes for the moving track (e.g. gap, track speed utility). */
  trackClassName?: string;
  /** Scroll direction. Adjacent rails should alternate. */
  direction?: "left" | "right";
  /** ms of inactivity after touch/scroll before the marquee resumes. */
  resumeDelay?: number;
};

/** Pad a short list, then duplicate it so a 50% CSS loop is seamless. */
export function duplicateForMarquee<T>(items: T[], min = 6): T[] {
  if (items.length === 0) return items;
  const padded =
    items.length >= min
      ? items
      : Array.from({ length: Math.ceil(min / items.length) }, () => items).flat();
  return [...padded, ...padded];
}

function resolveTrackClass(trackClassName: string, direction: "left" | "right") {
  if (direction !== "right") return trackClassName;
  return trackClassName
    .split(/\s+/)
    .map((cls) => {
      if (cls === "marquee-track-slow") return "marquee-track-slow-right";
      if (cls === "marquee-track") return "marquee-track-right";
      return cls;
    })
    .join(" ");
}

/**
 * Auto-scrolling marquee that reliably pauses on touch (tap, swipe, scroll)
 * and resumes cleanly once the user leaves the rail. Also honors hover/focus
 * pause via the CSS `.marquee-pause` utility.
 */
export default function MarqueeRail({
  children,
  className = "",
  trackClassName = "marquee-track gap-3 sm:gap-4",
  direction = "left",
  resumeDelay = 2600,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const [paused, setPaused] = useState(false);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const pauseNow = useCallback(() => {
    clearTimer();
    setPaused(true);
  }, []);

  const scheduleResume = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setPaused(false);
    }, resumeDelay);
  }, [resumeDelay]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = () => pauseNow();
    const onTouchMove = () => pauseNow();
    const onTouchEnd = () => scheduleResume();
    const onTouchCancel = () => scheduleResume();
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "touch" || e.pointerType === "pen") pauseNow();
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType === "touch" || e.pointerType === "pen") scheduleResume();
    };
    const onScroll = () => {
      pauseNow();
      scheduleResume();
    };
    const onVisibility = () => {
      if (document.hidden) pauseNow();
      else scheduleResume();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchCancel, { passive: true });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimer();
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pauseNow, scheduleResume]);

  return (
    <div
      ref={containerRef}
      className={`marquee-pause marquee-fade overflow-x-auto overflow-y-hidden no-scrollbar overscroll-x-contain [touch-action:pan-x_pan-y] [-webkit-overflow-scrolling:touch] -mx-1 px-1 py-2 ${paused ? "is-paused" : ""} ${className}`}
    >
      <div className={resolveTrackClass(trackClassName, direction)}>{children}</div>
    </div>
  );

}
